const SIGNED_QUERY_PATTERN = /(?:^|[?&])(?:X-Amz-|Signature=|AWSAccessKeyId=|Expires=|Credential=)/i;

function decodePath(value) {
  try {
    return decodeURIComponent(value);
  } catch (error) {
    return value;
  }
}

function hasTraversal(value) {
  return /(?:^|\/)\.\.(?:\/|$)/.test(decodePath(value));
}

function hasPrivateStorageMarker(url) {
  const hostnameSegments = url.hostname.toLowerCase().split('.');
  const pathSegments = decodePath(url.pathname)
    .toLowerCase()
    .split('/')
    .filter(Boolean);
  const unsafeSegment = (segment) =>
    segment === 'private' ||
    segment === 'kubiqa-private' ||
    segment.startsWith('private-') ||
    segment.endsWith('-private');

  return hostnameSegments.some(unsafeSegment) || pathSegments.some(unsafeSegment);
}

function normalizePathname(pathname) {
  return pathname.replace(/\/{2,}/g, '/').replace(/\/+$/, '');
}

export function validatePublicMediaBase(value, options = {}) {
  const required = options.required !== false;
  const rawValue = typeof value === 'string' ? value.trim() : '';

  if (!rawValue) {
    if (required) {
      throw new Error('Public media base configuration is required.');
    }

    return null;
  }

  if (rawValue.startsWith('//')) {
    throw new Error('Public media base must be an HTTPS URL.');
  }

  if (hasTraversal(rawValue)) {
    throw new Error('Public media base cannot contain path traversal.');
  }

  let url;
  try {
    url = new URL(rawValue);
  } catch (error) {
    throw new Error('Public media base must be a valid HTTPS URL.');
  }

  if (url.protocol !== 'https:') {
    throw new Error('Public media base must use HTTPS.');
  }

  if (url.username || url.password) {
    throw new Error('Public media base cannot contain credentials.');
  }

  if (url.search || url.hash) {
    throw new Error('Public media base cannot contain query or hash data.');
  }

  if (hasPrivateStorageMarker(url)) {
    throw new Error('Public media base cannot point at private storage.');
  }

  url.pathname = normalizePathname(url.pathname);

  return url.toString().replace(/\/$/, '');
}

function normalizePublicUrl(url, publicBase) {
  if (url.protocol !== 'https:' || url.username || url.password || url.search || url.hash) {
    return null;
  }

  if (hasTraversal(url.pathname) || hasPrivateStorageMarker(url)) {
    return null;
  }

  url.pathname = normalizePathname(url.pathname);
  const normalizedUrl = url.toString().replace(/\/$/, '');

  if (normalizedUrl === publicBase || normalizedUrl.startsWith(`${publicBase}/`)) {
    return normalizedUrl;
  }

  return null;
}

export function publicMediaUrl(value, base = process.env.VUE_APP_S3_BUCKET) {
  const publicBase = validatePublicMediaBase(base, { required: process.env.NODE_ENV === 'production' });
  const rawValue = typeof value === 'string' ? value.trim() : '';

  if (!publicBase || !rawValue || rawValue.startsWith('//') || SIGNED_QUERY_PATTERN.test(rawValue)) {
    return null;
  }

  if (hasTraversal(rawValue)) {
    return null;
  }

  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(rawValue)) {
    try {
      return normalizePublicUrl(new URL(rawValue), publicBase);
    } catch (error) {
      return null;
    }
  }

  if (rawValue.includes('?') || rawValue.includes('#')) {
    return null;
  }

  const objectPath = rawValue.replace(/^\/+/, '').replace(/\/{2,}/g, '/');

  if (!objectPath || hasTraversal(objectPath)) {
    return null;
  }

  const url = new URL(`${publicBase}/`);
  url.pathname = `${normalizePathname(url.pathname)}/${objectPath}`;

  return url.toString();
}
